import React from 'react';
import { Box, Typography, Card, CardContent, Button, Grid, Chip, keyframes, Tooltip, Stack } from "@mui/material";
import TagIcon from '@mui/icons-material/Tag';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveDatasetDisplayNames } from "../../../utils/datasetDisplay";

// FIXED: Continuous "Breathing" effect (Sine Wave) instead of a "Ping" that resets
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 206, 172, 0.7);
  }
  50% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(76, 206, 172, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 206, 172, 0.7);
  }
`;

// FIXED: seamless loop moving the full width of the 200% background
const shimmerAnimation = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const generateHardGradient = (colorsArr) => {
    if (!colorsArr || colorsArr.length === 0) return 'transparent';
    if (colorsArr.length === 1) return colorsArr[0];
    const step = 100 / colorsArr.length;
    const stops = colorsArr.map((color, idx) => {
        const start = idx * step;
        const end = (idx + 1) * step;
        return `${color} ${start}%, ${color} ${end}%`;
    });
    return `linear-gradient(to bottom, ${stops.join(', ')})`;
};

  const datasetColorsPalette = [
      
      '#3b82f6', // blue
     
      '#10b981', // emerald
      '#a855f7', // purple
      '#f59e0b', // amber
      '#0ea5e9', // sky
      '#84cc16', // lime
      '#ec4899', // pink
      'var(--fx-text-secondary)', // slate
      '#14b8a6', // teal
       '#f43f5e' // rose
  ];


const TrainingStatusCard = ({ 
  colors, 
  currentConfig, 
  trainingStatus, 
  segmentationStatus, 
  segmentationProgress,
  isCompleted, 
  hasBeenCleared,
  progress, 
  currentRound, 
  totalRounds, 
  selectedFeatures,
  getStatusColor,
  getStatusText,

  isRunning,
  featureMetadata, // Received from parent
  perDatasetFeaturesMap, // Added context dependency
  // dev-0.7.7+: pass the user's actual site-picker state (selectedSites)
  // from /configs alongside the capability registry so the Configuration
  // Details panel can resolve the correct dataset display name. The
  // /api/config response does NOT carry a `sites` field on dev-0.7.x,
  // only `resolved_sites` + `execution_assignment`; the form-state
  // `selectedSites` is the canonical source of "what the user picked".
  selectedSites,
  siteCapabilities,
  availableDatasets,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isSegmentation = currentConfig?.dataset_config?.processing_type === "imaging_segmentation";
  const rawStatus = isSegmentation ? segmentationStatus : trainingStatus?.status;
  // During mock playback there's no trainingStatus / segmentationStatus
  // coming from a backend, so `rawStatus` is undefined and the existing
  // progress UI used to stay dark. Derive a status from local run flags so
  // the bar, % completion, and Round n/m metrics light up during the mock
  // playback exactly the way they would for a real backend run.
  const currentStatus =
    rawStatus || (isCompleted ? "completed" : isRunning ? "running" : "idle");
  const segProgress = segmentationProgress?.percentage || 0;

  const isPreparing =
    isRunning &&
    !isCompleted &&
    !hasBeenCleared &&
    progress === 0 &&
    segProgress === 0;

  /* Progress Calculation Logic */
  // dev-0.7.16 — trust upstream `progress` prop.
  //
  // Pre-dev-0.7.16 this self-computed `(currentRound - 1) / totalRounds`
  // because the AppContext path was leaving Completion stuck at 0%
  // mid-flight (dev-0.7.7 BRCA workaround). On dev-0.7.16+ backend
  // publishes progress.percentage correctly per round (0/25/50/75/100
  // for a 4-round run) AND AppContext.fetchFlowerTaskStatus now passes
  // that value through verbatim. The (cur-1)/total formula here became
  // double-bookkeeping that under-counted by 1 round — most visible on
  // multi-class runs where the lag stacked with the round_metrics
  // publish lag and the UI looked like a 0% → 100% jump.
  //
  // dev-0.7.32 (round 2): removed the `if (isSegmentation) return
  // segmentationProgress?.percentage || 0` branch that was the seg
  // version of yesterday's BRCA banner-vs-card mismatch. Backend's new
  // imaging segmentation training flows through /execute_training_flower
  // (dev-0.7.21+) and updates trainingProgress / `progress` — the same
  // path tabular uses. `segmentationProgress` was the legacy M-seg.A
  // demo state field (Flask in-process torch, unregistered in dev-0.7.21)
  // and is never written under the live path, so reading it here pinned
  // Completion% to 0 throughout seg training. LaunchStatusBanner reads
  // `progress` directly with no segmentation special case, which is why
  // the right banner showed the correct % while the left card stayed at
  // 0 — exact same banner-vs-card asymmetry as the hasBeenCleared bug
  // we fixed yesterday, just on a different gate. The fix is symmetric:
  // delete the gate, use the same `progress` prop the banner uses.
  const getProgressValue = () => {
      if (currentStatus === 'completed' || isCompleted) {
        return 100;
      }
      return progress || 0;
  };

  /* Helper Functions for Filter Logic (Ported from Configs) */
  const inferFeatureType = (name) => {
    const lower = name.toLowerCase();
    if (lower.includes('age') || lower.includes('year') || lower.includes('count') || lower.includes('days') || lower.includes('id')) return 'INT';
    if (lower.includes('percent') || lower.includes('ratio') || lower.includes('rate') || lower.includes('mean') || lower.includes('std')) return 'FLOAT';
    if (lower.includes('is_') || lower.includes('has_') || lower.includes('grade') || lower.includes('stage') || lower.includes('status')) return 'CAT';
    return 'FLOAT'; // Default fallback
  };

  const isFeatureFiltered = (feature) => {
    // If no filter entry exists, it's not filtered
    // We check currentConfig.tabular_config.feature_filters since that's where it lives in the saved config
    const featureFilters = currentConfig?.tabular_config?.feature_filters || {};
    const filter = featureFilters[feature];
    
    if (!filter) return false;

    // If metadata doesn't exist, we can't judge (safest to check vs default)
    const meta = featureMetadata?.[feature];
    if (!meta) return false; 

    if (meta.type === 'numeric') {
        const min = Number(filter.min ?? meta.min);
        const max = Number(filter.max ?? meta.max);
        const metaMin = Number(meta.min);
        const metaMax = Number(meta.max);
        
        // It is filtered if min > meta.min OR max < meta.max
        return min > metaMin || max < metaMax; 
    } else if (meta.type === 'categorical') {
        const selectedValues = filter.values;
        if (!selectedValues || selectedValues.length === 0) return false; 
        
        // If selectedValues has same length as allowed_values, effectively not filtered
        if (meta.allowed_values && selectedValues.length === meta.allowed_values.length) return false;

        return true;
    }
    return false;
  };

  const getFilterSummary = (feature) => {
      if (!isFeatureFiltered(feature)) return "";
      const featureFilters = currentConfig?.tabular_config?.feature_filters || {};
      const filter = featureFilters[feature];
      const meta = featureMetadata?.[feature];
      
      if (!filter || !meta) return "";

      if (meta.type === 'numeric') {
          return `Range: ${filter.min ?? meta.min} - ${filter.max ?? meta.max}`;
      } else if (meta.type === 'categorical') {
          const vals = filter.values || [];
          if (vals.length === 0) return "All values";
          if (vals.length > 3) return `Includes: ${vals.slice(0, 3).join(', ')}...`;
          return `Includes: "${vals.join('", "')}"`;
      }
      return "";
  };

  const effectiveProgress = getProgressValue();
  const showProgressBackground = !hasBeenCleared && effectiveProgress > 0 && !isCompleted;

  return (
    <>
      {/* Training Status Card */}
      {/* ── Compact KPI strip — no duplicate "In Progress" text. The
            LaunchStatusBanner above is the single source of "we're running"
            feedback; this strip is just the live numbers. */}
      <Card
        sx={{
          mb: 2,
          backgroundColor: 'var(--fx-paper)',
          border: '1px solid var(--fx-border)',
          borderRadius: 2,
          boxShadow: 'var(--fx-shadow-sm)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle progress fill behind the KPIs while a run is active */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${effectiveProgress}%`,
            background: `linear-gradient(90deg,
              rgba(22, 163, 74, 0.05) 0%,
              rgba(22, 163, 74, 0.12) 50%,
              rgba(22, 163, 74, 0.05) 100%)`,
            backgroundSize: '200% 100%',
            animation: showProgressBackground ? `${shimmerAnimation} 2.5s infinite linear` : 'none',
            transition: 'width 0.8s cubic-bezier(0.65, 0, 0.35, 1)',
            zIndex: 0,
            display: (currentStatus === 'idle' || currentStatus === 'completed' || !currentStatus) ? 'none' : 'block',
          }}
        />

        <CardContent sx={{ position: 'relative', zIndex: 1, p: '14px 18px !important' }}>
          {(() => {
            const isSeg = currentConfig?.dataset_config?.processing_type === "imaging_segmentation";
            const total = isSeg ? (currentConfig?.app_config?.["num-server-rounds"] || totalRounds || 2) : totalRounds;
            const isActive = (currentStatus && currentStatus !== 'idle' && !hasBeenCleared) || isPreparing;
            // dev-0.7.32 (round 2): removed the `isActive ? ... : 0` gate on
            // pct so the displayed number mirrors LaunchStatusBanner exactly
            // (the banner just does `Math.round(progress)` with no extra
            // gate). With the gate in place, any scenario where isActive
            // evaluates false but `progress` is non-zero (transient state
            // races, imaging pipelines where currentStatus didn't propagate
            // cleanly, etc.) forced Completion% to 0 even though the banner
            // showed the real value. isActive remains used elsewhere
            // (bar color, shimmer animation) — only the number readout
            // now bypasses it.
            const pct = Math.max(0, Math.min(100, Math.round(effectiveProgress)));

            const protocolLabel = isSeg ? 'Segmentation'
              : (currentConfig?.dataset_config?.processing_type === 'imaging_classification' ? 'Imaging' : 'Tabular');
            const protocolColor = isSeg ? '#0f766e'
              : (currentConfig?.dataset_config?.processing_type === 'imaging_classification' ? '#0087B4' : '#1B365D');

            // Compact uniform label style
            const labelSx = { color: 'var(--fx-text-secondary)', fontWeight: 700, fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.09em', lineHeight: 1.2 };
            const valueSx = { color: 'var(--fx-text-primary)', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' };

            // Bar color reflects state
            const barColor = isCompleted ? '#16a34a' : isActive ? '#0087B4' : 'var(--fx-border-strong)';

            return (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.6fr) 1px minmax(0, 0.9fr) 1px minmax(0, 1fr)',
                  alignItems: 'center',
                  columnGap: 2,
                }}
              >
                {/* COMPLETION — single inline progress bar (clearly the only bar) */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={labelSx}>Completion</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                      <Typography sx={valueSx}>{pct}</Typography>
                      <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.75rem', fontWeight: 600 }}>%</Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      position: 'relative',
                      height: 6,
                      borderRadius: 999,
                      bgcolor: '#eef2f6',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        width: `${pct}%`,
                        background: pct === 0 ? 'transparent' : `linear-gradient(90deg, ${barColor}, ${barColor}cc)`,
                        transition: 'width 0.6s cubic-bezier(0.65, 0, 0.35, 1)',
                        borderRadius: 999,
                      }}
                    />
                    {isActive && pct > 0 && pct < 100 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: `${pct}%`,
                          background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)`,
                          backgroundSize: '200% 100%',
                          animation: `${shimmerAnimation} 2s infinite linear`,
                          borderRadius: 999,
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box sx={{ width: 1, height: 38, bgcolor: 'var(--fx-border)', justifySelf: 'center' }} />

                {/* ROUND */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                  <Typography sx={labelSx}>Round</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                    <Typography sx={valueSx}>{currentRound || 0}</Typography>
                    <Typography sx={{ color: 'var(--fx-text-secondary)', fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'var(--fx-font-mono)', fontVariantNumeric: 'tabular-nums' }}>
                      / {total || 'n/a'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ width: 1, height: 38, bgcolor: 'var(--fx-border)', justifySelf: 'center' }} />

                {/* PROTOCOL */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
                  <Typography sx={labelSx}>Protocol</Typography>
                  <Box>
                    <Chip
                      size="small"
                      label={protocolLabel}
                      sx={{
                        height: 22,
                        bgcolor: `${protocolColor}14`,
                        color: protocolColor,
                        border: `1px solid ${protocolColor}33`,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.01em',
                        '& .MuiChip-label': { px: 0.9 },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </CardContent>
      </Card>
        
      {/* Configuration Details Card — sticky behaviour is now handled by the
          left-column wrapper in execute/index.jsx so this card and the
          Pre-run Checks panel underneath scroll as a single sticky group. */}
      <Card sx={{
        mb: 2,
        backgroundColor: 'var(--fx-paper)',
        border: '1px solid var(--fx-border)',
        borderRadius: 2,
        boxShadow: 'var(--fx-shadow-sm)',
      }}>
        <CardContent sx={{ p: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TagIcon sx={{ color: '#0087B4', fontSize: 16 }} />
                <Typography sx={{
                  color: 'var(--fx-text-primary)',
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  letterSpacing: '-0.005em',
                }}>
                  {t('execute.configurationDetails')}
                </Typography>
              </Box>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/configs', {
                  state: {
                    dataset_path: currentConfig?.dataset_config?.dataset_path,
                    selected_datasets: currentConfig?.dataset_config?.selected_datasets
                  }
                })}
                sx={{
                  color: '#0087B4',
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  p: '2px 6px',
                  minWidth: 'auto',
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 135, 180, 0.08)',
                  }
                }}
              >
                {t('execute.edit')}
              </Button>
            </Box>
            
            <Grid container spacing={0}>
              {[
                { 
                  label: t('configs.processingType'),
                  value: (
                    <Chip 
                      label={(() => {
                        const processingType = currentConfig?.dataset_config?.processing_type || 'tabular';
                        if (processingType === 'imaging_classification') return t('configs.imagingMode');
                        if (processingType === 'imaging_segmentation') return t('configs.segmentationMode');
                        return t('configs.tabularMode');
                      })()}
                      color={(() => {
                        const processingType = currentConfig?.dataset_config?.processing_type || 'tabular';
                        if (processingType === 'imaging_classification') return 'secondary';
                        if (processingType === 'imaging_segmentation') return 'info';
                        return 'primary';
                      })()}
                      size="small"
                      sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )
                },
                {
                   label: t('execute.model'),
                   value: (() => {
                      const modelType = currentConfig?.dataset_config?.model_type;
                      const display = modelType || t('execute.notSet');
                      switch(modelType) {
                        case 'logreg': return 'Logistic Regression';
                        case 'mlp': return 'Multi-Layer Perceptron';
                        case 'cnn': return 'Convolutional Neural Network';
                        case 'lstm': return 'LSTM';
                        case 'transformer': return 'Transformer';
                        default: return display;
                      }
                   })()
                },
                {
                   label: t('execute.dataset'),
                   isCustomRow: true,
                   render: () => {
                     // dev-0.7.7+: prefer `selectedSites` (the live form-state
                     // from /configs) because /api/config response shape on
                     // dev-0.7.x does NOT include a top-level `sites` field —
                     // it has `resolved_sites` + `execution_assignment` only,
                     // so currentConfig.sites was always undefined.
                     //
                     // dev-0.7.32 (this change): stopped passing the legacy
                     // `selected_datasets` / `dataset_path` fallback. After a
                     // page refresh the form-state `selectedSites` is empty,
                     // and the backend's /api/config response carries stale
                     // simulation-mode artifacts in `dataset_config.selected_datasets`
                     // (e.g. ["dataset_primary_site_c1.csv",
                     // "dataset_primary_site_c2.csv"] — pre-F1.5 placeholder
                     // filenames from when the legacy mock simulation was the
                     // default flow). The resolver used to fall through to
                     // those filenames and surface them as "2 selected /
                     // dataset_primary_site_c1.csv / ..." in this panel,
                     // which is confusing because (a) the filenames are not
                     // what the user actually picked and (b) they aren't
                     // real artifacts on the current dev-0.7.x backend.
                     //
                     // With the fallback removed, the resolver returns
                     // names=[] when selectedSites is empty, and the
                     // "Not set" branch below renders a clean placeholder
                     // instead. The /configs Session Preview still uses the
                     // legacy fallback at its own call site for simulation-
                     // mode backward compat — we only suppressed it here.
                     const { names: datasetNames } = resolveDatasetDisplayNames({
                       sites: selectedSites,
                       siteCapabilities,
                       availableDatasets,
                     });

                     // Common row styling — kept in sync with the standard rows
                     // below (label left, value right, dashed bottom border)
                     const rowSx = {
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'space-between',
                       width: '100%',
                       py: 0.6,
                       borderBottom: '1px dashed var(--fx-border)',
                       gap: 1,
                     };
                     const labelSx = {
                       color: 'var(--fx-text-secondary)',
                       fontWeight: 500,
                       fontSize: '0.75rem',
                       whiteSpace: 'nowrap',
                     };
                     const valueSx = {
                       fontFamily: 'var(--fx-font-mono)',
                       color: 'var(--fx-text-strong)',
                       fontWeight: 600,
                       fontSize: '0.8125rem',
                       textAlign: 'right',
                     };

                     if (datasetNames.length === 0) {
                        return (
                          <Box sx={rowSx}>
                            <Typography sx={labelSx}>{t('execute.dataset')}</Typography>
                            <Typography sx={{ ...valueSx, color: 'var(--fx-text-secondary)' }}>{t('execute.notSet')}</Typography>
                          </Box>
                        );
                     }

                     if (datasetNames.length > 1) {
                         return (
                            <Box sx={{ width: '100%', py: 0.6, borderBottom: '1px dashed var(--fx-border)' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 0.6 }}>
                                    <Typography sx={labelSx}>
                                        {t('execute.dataset')}
                                    </Typography>
                                    <Typography sx={{ ...valueSx, color: 'var(--fx-text-secondary)' }}>
                                        {datasetNames.length} selected
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, pl: 0.5 }}>
                                    {datasetNames.map((name, idx) => (
                                        <Box key={`${name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: datasetColorsPalette[idx % datasetColorsPalette.length], flexShrink: 0 }} />
                                            <Box sx={{
                                                fontFamily: 'var(--fx-font-mono)',
                                                color: 'var(--fx-text-strong)',
                                                fontWeight: 600,
                                                fontSize: '0.8125rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {name}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                         );
                     }

                     return (
                       <Box sx={rowSx}>
                         <Typography sx={labelSx}>{t('execute.dataset')}</Typography>
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                           <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: datasetColorsPalette[0], flexShrink: 0 }} />
                           <Typography sx={{ ...valueSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                             {datasetNames[0]}
                           </Typography>
                         </Box>
                       </Box>
                     );
                   }
                },
                {
                   // dev-0.7.7+: render the user's site + capability_id pairs
                   // so it's clear which capability of the selected dataset
                   // each site is contributing. Source: selectedSites (UI
                   // form-state with {site_id, capability_id}) joined with
                   // siteCapabilities lookup for display_name.
                   label: 'Sites',
                   isCustomRow: true,
                   render: () => {
                     const rowSx = {
                       display: 'flex',
                       alignItems: 'flex-start',
                       justifyContent: 'space-between',
                       width: '100%',
                       py: 0.6,
                       borderBottom: '1px dashed var(--fx-border)',
                       gap: 1,
                     };
                     const labelSx = {
                       color: 'var(--fx-text-secondary)',
                       fontWeight: 500,
                       fontSize: '0.75rem',
                       whiteSpace: 'nowrap',
                       pt: 0.2,
                     };
                     const valueColSx = { display: 'flex', flexDirection: 'column', gap: 0.4, alignItems: 'flex-end', minWidth: 0 };
                     const completed = (selectedSites || []).filter(
                       (s) => s && s.site_id && s.capability_id
                     );
                     if (completed.length === 0) {
                       return (
                         <Box sx={rowSx}>
                           <Typography sx={labelSx}>Sites</Typography>
                           <Typography sx={{ fontFamily: 'var(--fx-font-mono)', color: 'var(--fx-text-secondary)', fontWeight: 600, fontSize: '0.8125rem' }}>
                             {t('execute.notSet')}
                           </Typography>
                         </Box>
                       );
                     }
                     return (
                       <Box sx={rowSx}>
                         <Typography sx={labelSx}>Sites ({completed.length})</Typography>
                         <Box sx={valueColSx}>
                           {completed.map((s) => {
                             const caps = siteCapabilities?.[s.site_id] || [];
                             const cap = caps.find((c) => c.capability_id === s.capability_id);
                             const siteName = cap?.site_display_name || s.site_id;
                             return (
                               <Box
                                 key={`${s.site_id}::${s.capability_id}`}
                                 sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
                               >
                                 <Typography
                                   sx={{
                                     fontFamily: 'var(--fx-font-mono)',
                                     color: 'var(--fx-text-strong)',
                                     fontWeight: 600,
                                     fontSize: '0.8125rem',
                                     lineHeight: 1.3,
                                   }}
                                 >
                                   {siteName}
                                 </Typography>
                                 <Typography
                                   sx={{
                                     fontFamily: 'var(--fx-font-mono)',
                                     color: 'var(--fx-text-secondary)',
                                     fontWeight: 500,
                                     fontSize: '0.7rem',
                                     lineHeight: 1.3,
                                   }}
                                 >
                                   {s.capability_id}
                                 </Typography>
                               </Box>
                             );
                           })}
                         </Box>
                       </Box>
                     );
                   }
                },
                {
                   label: t('execute.rounds'),
                   value: currentConfig?.app_config?.["num-server-rounds"] || t('execute.notSet')
                },
                {
                   label: t('execute.localEpochs'),
                   value: currentConfig?.app_config?.["local-epochs"] || t('execute.notSet')
                },
                {
                   label: t('execute.batchSize'),
                   value: currentConfig?.app_config?.["batch-size"] || t('execute.notSet')
                },
                {
                   label: t('execute.learningRate'),
                   value: currentConfig?.app_config?.["learning-rate"] || t('execute.notSet')
                }
              ].map((item, index, arr) => (
                  <Grid item xs={12} key={index}>
                    {item.isCustomRow ? (
                        item.render()
                    ) : (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          py: 0.6,
                          minHeight: 32,
                          borderBottom: index === arr.length - 1 ? 'none' : '1px dashed var(--fx-border)',
                          gap: 1,
                        }}>
                          <Typography sx={{
                            color: 'var(--fx-text-secondary)',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                          }}>
                            {item.label}
                          </Typography>
                          <Box sx={{
                            fontFamily: typeof item.value === 'string' || typeof item.value === 'number' ? 'var(--fx-font-mono)' : 'inherit',
                            color: 'var(--fx-text-strong)',
                            fontWeight: 600,
                            fontSize: '0.8125rem',
                            textAlign: 'right',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                          }}>
                            {item.value}
                          </Box>
                        </Box>
                    )}
                  </Grid>
              ))}
              
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default TrainingStatusCard;